if (!customElements.get('variant-gallery')) {
  class VariantGallery extends HTMLElement {
    constructor() {
      super();
      this.init();
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initializeGallery());
      } else {
        this.initializeGallery();
      }
    }

    initializeGallery() {
      this.mediaList = this.querySelector('.product__media-gallery');
      if (!this.mediaList) return;

      this.mediaItems = Array.from(this.querySelectorAll('.product__media-item'));

      // Scope variant-selects to the nearest product-info (works in both the full
      // product page and the quick-add modal without grabbing the wrong element).
      this.variantSelects =
        this.closest('product-info')?.querySelector('variant-selects') ||
        this.querySelector('variant-selects') ||
        document.querySelector('variant-selects');

      this.showAllImages();
      this.setupThumbnailClicks();

      if (this.variantSelects) {
        this.setupVariantListeners();
      }
    }

    showAllImages() {
      this.mediaItems.forEach((item, index) => {
        item.style.visibility = 'visible';
        item.style.opacity = '1';
        item.style.display = 'block';
        if (index === 0) item.classList.add('featured-media');
      });
    }

    setupThumbnailClicks() {
      this.mediaItems.forEach((item, index) => {
        if (index > 0) {
          item.addEventListener('click', () => this.onThumbnailClick(item));
        }
      });
    }

    setupVariantListeners() {
      // variantChange fires after product-info.js completes the section fetch and
      // has the fully-resolved variant object (including featured_media).
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data?.variant) {
          this.handleVariantChange(event.data.variant);
        }
      });

      // Initialise with whatever variant is already selected.
      const variantJson = this.variantSelects.querySelector('[data-selected-variant]');
      if (variantJson) {
        try {
          this.handleVariantChange(JSON.parse(variantJson.textContent));
        } catch (e) {
          // malformed JSON — leave gallery as-is
        }
      }
    }

    disconnectedCallback() {
      this.variantChangeUnsubscriber?.();
    }

    handleVariantChange(variant) {
      if (!variant) {
        this.showAllImages();
        return;
      }

      const options = [variant.option1, variant.option2, variant.option3].filter(Boolean);
      const orientation = options
        .find((o) => o.toLowerCase().includes('portrait') || o.toLowerCase().includes('landscape'))
        ?.toLowerCase();
      const colorOption = options
        .find((o) => !o.toLowerCase().includes('portrait') && !o.toLowerCase().includes('landscape'))
        ?.toLowerCase();

      let targetItem = null;

      // --- Step 1: featured_media.id ---
      // The template stamps data-media-id="{{ media.id }}" on every .product__media-item.
      // variant.featured_media.id is the same raw numeric ID, so this gives a direct
      // variant-to-image match. Works perfectly when each variant has a unique image
      // (e.g. colour variants, multi-option DM screen).
      const featuredMediaId = variant.featured_media?.id;
      if (featuredMediaId != null) {
        targetItem = this.mediaItems.find((item) => item.dataset.mediaId == featuredMediaId);
      }

      // --- Step 2: orientation alt-text refinement ---
      // Some products (e.g. bundles) assign the same image to both Portrait and Landscape
      // variants, so Step 1 can’t distinguish them. However, the product’s GALLERY may
      // contain separate portrait/landscape photos whose alt text includes those keywords.
      // If we can find such a photo, prefer it over the Step 1 result.
      if (orientation) {
        const orientationMatch = this.mediaItems.find((item) => {
          const alt = (item.querySelector('img')?.alt || '').toLowerCase();
          if (!alt.includes('portrait') && !alt.includes('landscape')) return false;
          if (!alt.includes(orientation)) return false;
          // If the image also carries a color spec, it must match the selected color.
          if (colorOption && alt.includes('color:')) {
            return alt.includes(`color:${colorOption}`);
          }
          return true;
        });
        if (orientationMatch) targetItem = orientationMatch;
      }

      // --- Step 3: data-variant-color fallback ---
      // Used when featured_media is absent and there’s no orientation alt-text match.
      if (!targetItem && colorOption) {
        targetItem = this.mediaItems.find(
          (item) => item.dataset.variantColor?.toLowerCase() === colorOption
        );
      }

      if (!targetItem) return;
      this.updateFeaturedImage(targetItem);
    }

    updateFeaturedImage(newItem) {
      if (!newItem) return;

      const currentFeatured = this.querySelector('.featured-media');
      if (currentFeatured === newItem) return;

      if (currentFeatured) currentFeatured.classList.remove('featured-media');

      const newFeatured = newItem.cloneNode(true);
      newFeatured.classList.add('featured-media');

      if (currentFeatured) {
        this.mediaList.replaceChild(newFeatured, currentFeatured);
      } else {
        this.mediaList.insertBefore(newFeatured, this.mediaList.firstChild);
      }
    }

    onThumbnailClick(clickedItem) {
      if (clickedItem.style.display === 'none') return;
      this.updateFeaturedImage(clickedItem);
    }
  }

  customElements.define('variant-gallery', VariantGallery);
}
